import type { SafeParseReturnType } from 'zod'
import type {
  AddressCoordinates,
  GoogleAddress,
  GoogleAddressCompact,
  UserAddress,
  UserAddressNew,
} from '../entities'
import { GoogleAddressCompactSchema, GoogleAddressSchema, UserAddressSchema } from '../entities'
import { createNewUserAddressFromGoogleAddress, normalizedAddressFieldsForBackend } from './helpers'
import type {
  CreateUserAddressResponse,
  GetUserAddressesResponse,
  SearchAddressesResponse,
} from './types'
import { normalizeLocationCoordinates } from '@/location/use-cases'
import { getConfig } from '@/tools/config'
import { fetcher } from '@/tools/fetcher/client'

export const getAddressByCoordinates = async (
  coordinates: AddressCoordinates,
  isAuthUser: boolean = false
): Promise<UserAddressNew> => {
  const { apiUrl } = getConfig()
  const { longitude, latitude } = normalizeLocationCoordinates(coordinates)

  const response = await fetcher.get<GoogleAddress>(
    `${apiUrl}/v3/addresslookup/${isAuthUser ? '' : 'pub/'}coordinates?latitude=${latitude}&longitude=${longitude}`
  )
  const googleAddress = GoogleAddressSchema.parse(response.data)
  return createNewUserAddressFromGoogleAddress(googleAddress)
}

export const getAddressByPlaceId = async (
  placeId: string,
  isAuthUser: boolean = false
): Promise<UserAddressNew> => {
  const { apiUrl } = getConfig()
  const response = await fetcher.get<GoogleAddress>(
    `${apiUrl}/v3/addresslookup/${isAuthUser ? '' : 'pub/'}${placeId}`
  )
  const googleAddress = GoogleAddressSchema.parse(response.data)
  return createNewUserAddressFromGoogleAddress(googleAddress)
}

export const searchAddresses = async (
  query: string,
  isAuthUser: boolean = false
): Promise<GoogleAddressCompact[]> => {
  const { apiUrl } = getConfig()
  const encodedQuery = encodeURI(query)
  const response = await fetcher.get<SearchAddressesResponse>(
    `${apiUrl}/v3/addresslookup/${isAuthUser ? '' : 'pub/'}address?address=${encodedQuery}`
  )
  const parseResult: SafeParseReturnType<GoogleAddressCompact[], GoogleAddressCompact[]> =
    GoogleAddressCompactSchema.array().safeParse(response.data.addresses)

  return parseResult.success ? parseResult.data : []
}

export const getUserAddresses = async (): Promise<UserAddress[]> => {
  const { apiUrl } = getConfig()

  const response = await fetcher.get<GetUserAddressesResponse>(
    `${apiUrl}/customer_profile/api/v1/address_book/me/addresses`
  )
  const addresses = response.data.data.addresses
  const savedAddresses: UserAddress[] = addresses
    .filter((i) => i.entryType === 'SAVED_ADDRESS')
    .map((i) => i.address)
  return UserAddressSchema.array().parse(savedAddresses)
}

export const createUserAddress = async (address: UserAddressNew): Promise<UserAddress['id']> => {
  const { apiUrl } = getConfig()
  const payload = {
    address: {
      ...address,
      fields: normalizedAddressFieldsForBackend(address.fields),
    },
  }
  const response = await fetcher.post<CreateUserAddressResponse>(
    `${apiUrl}/customer_profile/api/v1/address_book/addresses`,
    payload
  )
  return response.data.data.newAddressId
}

export const updateUserAddress = async (address: UserAddress): Promise<void> => {
  const { apiUrl } = getConfig()
  const payload = {
    ...address,
    fields: normalizedAddressFieldsForBackend(address.fields),
  }
  await fetcher.patch(
    `${apiUrl}/customer_profile/api/v1/address_book/addresses/${address.id}`,
    payload
  )
}

export const deleteUserAddress = async (addressId: UserAddress['id']): Promise<void> => {
  const { apiUrl } = getConfig()
  await fetcher.delete(`${apiUrl}/customer_profile/api/v1/address_book/addresses/${addressId}`)
}

export const getGooglePlaceIdFromCoordinates = async (
  coordinates: AddressCoordinates,
  isAuthUser: boolean = false
): Promise<string> => {
  const { apiUrl } = getConfig()
  const googleAddressResponse = await fetcher.get<GoogleAddress>(
    `${apiUrl}/v3/addresslookup/${isAuthUser ? '' : 'pub/'}coordinates?latitude=${coordinates.latitude}&longitude=${coordinates.longitude}`
  )
  const { placeId } = googleAddressResponse.data

  return placeId
}
